import React, { useState } from 'react';
import styled from 'styled-components';
import { ShoppingBag, Shield, Truck, CreditCard, HeadphonesIcon, Star } from 'lucide-react';
import ContentLayout from '../../Common/Component/ContentLayout';

const Container = styled.div`
  width: 100%;
  line-height: 1.6;
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
  border-bottom: 2px solid #e5e7eb;
`;

const Tab = styled.button`
  padding: 12px 24px;
  border: none;
  background: none;
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.active ? '#111827' : '#6b7280'};
  border-bottom: 2px solid ${props => props.active ? '#111827' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s;
  margin: 0 10px;
  
  &:hover {
    color: #111827;
  }
`;

const ContentSection = styled.div`
  display: ${props => props.active ? 'block' : 'none'};
  animation: fadeIn 0.3s ease-in-out;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 40px;
`;

const FeatureCard = styled.div`
  background: #fff;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  text-align: center;
  transition: transform 0.2s;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #111827;
`;

const FeatureTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #111827;
`;

const FeatureDescription = styled.p`
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
`;

const GuideSection = styled.div`
  background: #f9fafb;
  padding: 30px;
  border-radius: 12px;
  margin-bottom: 30px;
`;

const GuideTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #111827;
`;

const StepList = styled.ol`
  list-style: none;
  counter-reset: step-counter;
`;

const StepItem = styled.li`
  counter-increment: step-counter;
  margin-bottom: 20px;
  position: relative;
  padding-left: 50px;
  
  &::before {
    content: counter(step-counter);
    position: absolute;
    left: 0;
    top: 0;
    width: 30px;
    height: 30px;
    background: #111827;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 14px;
  }
`;

const StepTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #111827;
`;

const StepDescription = styled.p`
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
`;

const PolicySection = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 25px;
  margin-bottom: 20px;
`;

const PolicyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #111827;
`;

const PolicyContent = styled.div`
  color: #374151;
  font-size: 14px;
  line-height: 1.6;
  
  ul {
    margin: 10px 0;
    padding-left: 20px;
  }
  
  li {
    margin-bottom: 5px;
  }
`;

function SystemGuide() {
  const [activeTab, setActiveTab] = useState('overview');

  const features = [
    {
      icon: <ShoppingBag size={24} />,
      title: "간편한 쇼핑",
      description: "직관적인 인터페이스로 원하는 상품을 빠르게 찾고 주문할 수 있습니다."
    },
    {
      icon: <Shield size={24} />,
      title: "안전한 결제",
      description: "SSL 암호화와 다양한 결제 수단으로 안전하게 결제할 수 있습니다."
    },
    {
      icon: <Truck size={24} />,
      title: "빠른 배송",
      description: "전국 당일/익일 배송 서비스로 빠르게 상품을 받아보실 수 있습니다."
    },
    {
      icon: <CreditCard size={24} />,
      title: "다양한 혜택",
      description: "회원 등급별 할인과 적립금, 쿠폰 혜택을 제공합니다."
    },
    {
      icon: <HeadphonesIcon size={24} />,
      title: "고객지원",
      description: "친절한 고객센터에서 언제든지 도움을 받으실 수 있습니다."
    },
    {
      icon: <Star size={24} />,
      title: "품질보장",
      description: "엄선된 상품과 품질보장으로 안심하고 구매하실 수 있습니다."
    }
  ];

  const shoppingSteps = [
    {
      title: "회원가입 및 로그인",
      description: "AntsMarket에 회원가입 후 로그인하여 개인화된 쇼핑을 시작하세요."
    },
    {
      title: "상품 검색 및 선택",
      description: "카테고리나 검색을 통해 원하는 상품을 찾고 상세정보를 확인하세요."
    },
    {
      title: "장바구니 담기",
      description: "구매하고 싶은 상품을 장바구니에 담고 수량을 조정하세요."
    },
    {
      title: "주문 및 결제",
      description: "배송지 정보를 입력하고 원하는 결제수단으로 결제를 진행하세요."
    },
    {
      title: "배송 및 수령",
      description: "주문 확인 후 배송이 시작되며, 배송 현황을 실시간으로 확인할 수 있습니다."
    }
  ];

  return (
    <ContentLayout 
      title="AntsMarket 이용가이드"
      subtitle="쇼핑몰 이용방법과 정책을 안내해드립니다"
      centerTitle={false}
    >
      <Container>

      <TabContainer>
        <Tab active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
          서비스 소개
        </Tab>
        <Tab active={activeTab === 'guide'} onClick={() => setActiveTab('guide')}>
          이용가이드
        </Tab>
        <Tab active={activeTab === 'policy'} onClick={() => setActiveTab('policy')}>
          이용약관
        </Tab>
      </TabContainer>

      <ContentSection active={activeTab === 'overview'}>
        <GuideTitle>AntsMarket의 특징</GuideTitle>
        <FeatureGrid>
          {features.map((feature, index) => (
            <FeatureCard key={index}>
              <IconWrapper>{feature.icon}</IconWrapper>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeatureGrid>
      </ContentSection>

      <ContentSection active={activeTab === 'guide'}>
        <GuideSection>
          <GuideTitle>쇼핑 이용가이드</GuideTitle>
          <StepList>
            {shoppingSteps.map((step, index) => (
              <StepItem key={index}>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </StepItem>
            ))}
          </StepList>
        </GuideSection>

        <GuideSection>
          <GuideTitle>주요 기능 안내</GuideTitle>
          <PolicyContent>
            <h4>• 장바구니 기능</h4>
            <p>상품을 장바구니에 담아 나중에 구매하거나, 여러 상품을 한번에 주문할 수 있습니다.</p>
            
            <h4>• 위시리스트</h4>
            <p>관심 있는 상품을 위시리스트에 저장하여 나중에 쉽게 찾아볼 수 있습니다.</p>
            
            <h4>• 쿠폰 및 적립금</h4>
            <p>할인 쿠폰과 적립금을 활용하여 더욱 저렴하게 쇼핑하실 수 있습니다.</p>
            
            <h4>• 주문내역 조회</h4>
            <p>마이페이지에서 지난 주문내역과 배송상황을 확인할 수 있습니다.</p>
          </PolicyContent>
        </GuideSection>
      </ContentSection>

      <ContentSection active={activeTab === 'policy'}>
        <PolicySection>
          <PolicyTitle>개인정보 처리방침</PolicyTitle>
          <PolicyContent>
            <p>AntsMarket은 개인정보보호법에 따라 이용자의 개인정보 보호 및 권익을 보호하고 개인정보와 관련한 이용자의 고충을 원활하게 처리할 수 있도록 다음과 같은 처리방침을 두고 있습니다.</p>
            <ul>
              <li>수집하는 개인정보 항목: 이름, 연락처, 이메일, 배송지 정보</li>
              <li>개인정보 수집 및 이용목적: 서비스 제공, 주문 처리, 고객지원</li>
              <li>개인정보 보유 및 이용기간: 회원탈퇴 시까지 또는 관계법령에 따른 보존기간</li>
            </ul>
          </PolicyContent>
        </PolicySection>

        <PolicySection>
          <PolicyTitle>이용약관</PolicyTitle>
          <PolicyContent>
            <h4>제1조 (목적)</h4>
            <p>이 약관은 AntsMarket에서 제공하는 인터넷 관련 서비스의 이용조건 및 절차에 관한 사항을 규정함을 목적으로 합니다.</p>
            
            <h4>제2조 (회원가입)</h4>
            <ul>
              <li>만 14세 이상의 개인 또는 법인이 가입할 수 있습니다.</li>
              <li>허위정보 기입 시 서비스 이용이 제한될 수 있습니다.</li>
            </ul>
            
            <h4>제3조 (서비스 이용)</h4>
            <ul>
              <li>서비스는 연중무휴, 1일 24시간 제공됩니다.</li>
              <li>시스템 점검 등 불가피한 경우 서비스가 일시 중단될 수 있습니다.</li>
            </ul>
          </PolicyContent>
        </PolicySection>

        <PolicySection>
          <PolicyTitle>배송 및 교환환불 정책</PolicyTitle>
          <PolicyContent>
            <h4>배송정보</h4>
            <ul>
              <li>배송비: 30,000원 이상 구매 시 무료배송</li>
              <li>배송기간: 주문일로부터 1-2일 (주말/공휴일 제외)</li>
              <li>배송지역: 전국 (도서산간 지역 추가배송비 발생)</li>
            </ul>
            
            <h4>교환/환불</h4>
            <ul>
              <li>교환/환불 기간: 상품 수령일로부터 7일 이내</li>
              <li>교환/환불 조건: 미사용 상품, 원래 포장상태 유지</li>
              <li>교환/환불 불가: 개인 맞춤 상품, 식품, 위생용품 등</li>
            </ul>
          </PolicyContent>
        </PolicySection>
      </ContentSection>
      </Container>
    </ContentLayout>
  );
}

export default SystemGuide;